import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { AppTile } from '@app/models/app-tile';
import { TimeZoneItem } from '@app/models/time-zone-item';
import { UserAppTile } from '@app/models/user-app-tile';
import { CacheService } from '@app/services/cache.service';
import { CultureService } from '@app/services/culture.service';
import { format } from 'date-fns';
import { BehaviorSubject, concat, Observable, of, Subject } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, takeUntil, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SmartLinksService {

  private getUserSmartLinks$: Observable<Array<AppTile>>;
  private getAllSmartLinks$: Observable<Array<AppTile>>;
  private getSystemTimeZones$: Observable<Array<TimeZoneItem>>;

  public readonly cacheKeyPrefix = 'SmartLinksService-smartlinks-';
  public useCache = true;
  private reload$ = new Subject<void>();
  public refresh$ = new BehaviorSubject(null);

  constructor(private http: HttpClient, private cache: CacheService, private cultureService: CultureService, @Inject(LOCALE_ID) private localeId: string, private httpHelper: HttpHelper) { }

  public reload() {
    this.reload$.next();
    this.getUserSmartLinks$ = null;
    this.getAllSmartLinks$ = null;

    if (this.useCache) {
      this.cache.removeAllItemsInPersistentCacheWithGeneratedKeyPrefix(this.cacheKeyPrefix);
    }
    this.refresh$.next(null);
  }

  /** Gets smart links for a user (for my board) */
  getUserSmartLinks(): Observable<Array<AppTile>> {
    const cacheKey = this.cacheKeyPrefix + 'my';

    if (this.getUserSmartLinks$) {
      return this.getUserSmartLinks$;
    } else {
      this.getUserSmartLinks$ = this.http.get<Array<UserAppTile>>(`${Constants.API_ENDPOINT}/me/UserAppTiles/smartlinks`, {

      })
        .pipe(
          map(response => {
            console.log(`getUserSmartLinks: `, response);
            const userTiles = response.map(p => new UserAppTile(p));
            return userTiles.map(t => new AppTile(t.AppTile));
          }),
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getUserSmartLinks', result: new Array<AppTile>(), cacheKey: cacheKey })),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<AppTile>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getUserSmartLinks$);
        }
      }
      return this.getUserSmartLinks$;
    }
  }

  /** Gets all smart links, used when fetching for app center. */
  getAdminSmartLinks(): Observable<Array<AppTile>> {
    const cacheKey = this.cacheKeyPrefix + 'all';

    const httpParams = new HttpParams()
      .append('smartlinks', 'true');

    if (this.getAllSmartLinks$) {
      return this.getAllSmartLinks$;
    } else {
      this.getAllSmartLinks$ = this.http.get<Array<AppTile>>(`${Constants.API_ENDPOINT}/apptiles`, {
        params: httpParams,
      })
        .pipe(
          map(response => {
            console.log(`getAdminSmartLinks: `, response);
            return response.map(p => new AppTile(p));
          }),
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getAdminSmartLinks', result: new Array<AppTile>() })),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<AppTile>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getAllSmartLinks$);
        }
      }

      return this.getAllSmartLinks$;
    }
  }

  guessCurrentTimeZone(zones: Array<TimeZoneItem>): TimeZoneItem {
    // Since most customers are swedish we always try this first to make it quick and easy
    if (this.localeId === 'sv') {
      const stockholmZone = zones.find(z => z.IANAName.toLowerCase().indexOf('stockholm') !== -1 || z.DisplayName.toLowerCase().indexOf('stockholm') !== -1);
      if (stockholmZone) {
        return stockholmZone;
      }
    }

    // The following logic has been copied nad modified from Moment timezone https://github.com/moment/moment-timezone/blob/develop/moment-timezone.js#L335
    try {
      const intlName = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (intlName && intlName.length > 3) {
        const timeZoneItem = zones.find(z => z.IANAName.toLowerCase() === intlName.toLowerCase());
        if (timeZoneItem) {
          return timeZoneItem;
        }
      }
    } catch (e) {
      // Intl unavailable, fall back to manual guessing.
    }

    // Narrow down zones by trying to match the offset value https://date-fns.org/v2.9.0/docs/format
    const localTimeOffset = format(new Date(), 'xxx');
    const matchingZones = zones.filter(r => r.DisplayName.indexOf(localTimeOffset) !== -1);
    // force en-us so get the zone name in english to try and match with windows names
    // zone will look like this in working browsers: "1/29/2020, Central European Standard Time"
    let zone = new Date().toLocaleDateString('en-us', { timeZoneName: 'long' });
    if (zone && zone.indexOf(',') > -1) {
      zone = zone.split(',')[1];
      const matchingZone = matchingZones.find(z => z.StandardName.toLowerCase() === zone.trim().toLowerCase());
      if (matchingZone) {
        return matchingZone;
      }
    }
    // Fall back to the first one with the same UTC offset.
    return matchingZones[0];
  }

  getSystemTimeZones(): Observable<Array<TimeZoneItem>> {
    if (this.getSystemTimeZones$) {
      return this.getSystemTimeZones$;
    } else {
      this.getSystemTimeZones$ = this.cultureService.getRegionCodeForCurrentLocale()
        .pipe(
          mergeMap(regionCode => {
            const params = new HttpParams().append('regionCode', regionCode);
            return this.http.get<Array<TimeZoneItem>>(`${Constants.API_ENDPOINT}/datetime/zones`, {
              params: params,
            });
          }),
          catchError(this.httpHelper.handleError({ operation: 'getSystemTimeZones', result: new Array<TimeZoneItem>() })),
          shareReplay({ bufferSize: 1, refCount: true })
        );

      return this.getSystemTimeZones$;
    }
  }

}
